import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Badge, LoadingSpinner, Alert, ProgressBar } from 'components/common';
import { FaUsers, FaChartLine, FaUserCheck, FaUserTimes, FaUserPlus, FaClock, FaSitemap, FaUserShield } from 'react-icons/fa';
import { MdOnlinePrediction } from 'react-icons/md';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { getUserDashboardStats } from 'redux/authorization/userSlice';
import dayjs from 'helpers/dayjs';
import './UserDashboard.scss';
import { Link } from 'react-router-dom';

const SummaryCard = ({ title, value, color, icon, gradientFrom, gradientTo, subtitle }) => {
  return (
    <div className="summary-card tw-h-full tw-shadow-sm">
      <div className="tw-p-3">
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
          <div className={`summary-icon-wrapper bg-gradient-${color}`}>
            {React.createElement(icon, { className: "summary-icon" })}
          </div>
          <div
            className="summary-badge"
            style={{
              background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`
            }}
          >
            <span className="summary-badge-dot"></span>
          </div>
        </div>
        <div className="summary-content">
          <h3 className="summary-value tw-mb-1">{value.toLocaleString()}</h3>
          <p className="summary-title tw-text-slate-500 tw-mb-0">{title}</p>
          {subtitle && <small className="summary-subtitle tw-text-slate-500">{subtitle}</small>}
        </div>
        <div
          className="summary-progress-bar"
          style={{
            background: `linear-gradient(90deg, ${gradientFrom} 0%, ${gradientTo} 100%)`
          }}
        ></div>
      </div>
    </div>
  );
};

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  gradientFrom: PropTypes.string.isRequired,
  gradientTo: PropTypes.string.isRequired,
  subtitle: PropTypes.string
};

const UserDashboard = () => {
  const dispatch = useDispatch();
  const { dashboardStats = {} } = useSelector((state) => state.authorization_user || {});
  const {
    loading = false,
    error = null,
    statistics = null,
    recently_active = [],
    frequently_accessed = [],
    inactive_with_platform_access = []
  } = dashboardStats;

  useEffect(() => {
    dispatch(getUserDashboardStats());
  }, [dispatch]);

  const summaryCards = useMemo(() => {
    if (!statistics) return [];

    return [
      {
        title: "Total Users",
        value: statistics.total || 0,
        color: "primary",
        icon: FaUsers,
        gradientFrom: "#667eea",
        gradientTo: "#764ba2"
      },
      {
        title: "Active Users",
        value: statistics.active || 0,
        color: "success",
        icon: FaUserCheck,
        gradientFrom: "#4facfe",
        gradientTo: "#00f2fe",
        subtitle: `${statistics.active && statistics.total ? ((statistics.active / statistics.total) * 100).toFixed(1) : 0}% of total`
      },
      {
        title: "Deactivated",
        value: statistics.deactivated || 0,
        color: "danger",
        icon: FaUserTimes,
        gradientFrom: "#f093fb",
        gradientTo: "#f5576c"
      },
      {
        title: "Online Now",
        value: statistics.online || 0,
        color: "info",
        icon: MdOnlinePrediction,
        gradientFrom: "#43e97b",
        gradientTo: "#38f9d7"
      },
      {
        title: "New This Month",
        value: statistics.created_this_month || 0,
        color: "warning",
        icon: FaUserPlus,
        gradientFrom: "#fa709a",
        gradientTo: "#fee140"
      },
      {
        title: "Active Last 30 Days",
        value: statistics.active_last_30_days || 0,
        color: "secondary",
        icon: FaClock,
        gradientFrom: "#6c757d",
        gradientTo: "#495057",
        subtitle: `${statistics.active_last_30_days && statistics.total ? ((statistics.active_last_30_days / statistics.total) * 100).toFixed(1) : 0}% engagement`
      }
    ];
  }, [statistics]);

  // Monthly growth chart
  const monthlyGrowthChart = useMemo(() => {
    if (!statistics?.monthly_growth) return {};

    const data = statistics.monthly_growth;
    const months = data.map(item => item.month);
    const totalUsers = data.map(item => item.count);
    const activeUsers = data.map(item => item.active);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: {
          color: '#1e293b'
        }
      },
      legend: {
        data: ['New Users', 'Active Users'],
        bottom: 5,
        padding: [8, 20],
        icon: 'circle',
        iconSize: 10
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLine: {
          lineStyle: {
            color: '#cbd5e1'
          }
        },
        axisLabel: {
          color: '#64748b',
          fontSize: 11,
          rotate: 30
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#64748b',
          fontSize: 12
        },
        splitLine: {
          lineStyle: {
            color: '#f1f5f9',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'New Users',
          type: 'bar',
          data: totalUsers,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ]
            },
            borderRadius: [8, 8, 0, 0]
          },
          barWidth: '40%'
        },
        {
          name: 'Active Users',
          type: 'line',
          data: activeUsers,
          smooth: true,
          symbolSize: 6,
          symbol: 'circle',
          lineStyle: {
            width: 2.5,
            color: '#4facfe'
          },
          itemStyle: {
            color: '#4facfe',
            borderColor: '#fff',
            borderWidth: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(79, 172, 254, 0.3)' },
                { offset: 1, color: 'rgba(79, 172, 254, 0.05)' }
              ]
            }
          }
        }
      ],
      animationDuration: 800,
      animationEasing: 'cubicOut'
    };
  }, [statistics]);

  // Users by site pie chart
  const usersBySiteChart = useMemo(() => {
    if (!statistics?.users_by_site || statistics.users_by_site.length === 0) return {};

    const data = statistics.users_by_site.map(item => ({
      name: item.site,
      value: item.count
    }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: {
          color: '#1e293b'
        }
      },
      legend: {
        orient: 'vertical',
        right: 0,
        top: 'center',
        icon: 'circle',
        iconSize: 10,
        textStyle: {
          color: '#64748b',
          fontSize: 11
        },
        padding: [10, 15],
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 4,
        borderColor: '#e2e8f0',
        borderWidth: 1,
        type: 'scroll',
        scrollDataIndex: 0,
        pageIconColor: '#667eea',
        pageIconInactiveColor: '#cbd5e1',
        pageTextStyle: {
          color: '#64748b'
        }
      },
      series: [
        {
          name: 'Users by Site',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['30%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
              formatter: '{b}\n{c} users'
            }
          },
          labelLine: {
            show: false
          },
          data: data
        }
      ],
      color: ['#667eea', '#4facfe', '#43e97b', '#fa709a', '#f093fb', '#6c757d', '#764ba2', '#38f9d7']
    };
  }, [statistics]);

  // Users by role bar chart
  const usersByRoleChart = useMemo(() => {
    if (!statistics?.users_by_role || statistics.users_by_role.length === 0) return {};

    const data = statistics.users_by_role;
    const roles = data.map(item => item.role);
    const counts = data.map(item => item.count);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: {
          color: '#1e293b'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: roles,
        axisLine: {
          lineStyle: {
            color: '#cbd5e1'
          }
        },
        axisLabel: {
          color: '#64748b',
          fontSize: 11,
          rotate: 30
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#64748b',
          fontSize: 12
        },
        splitLine: {
          lineStyle: {
            color: '#f1f5f9',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Users',
          type: 'bar',
          data: counts,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#f093fb' },
                { offset: 1, color: '#f5576c' }
              ]
            },
            borderRadius: [8, 8, 0, 0]
          },
          barWidth: '50%'
        }
      ]
    };
  }, [statistics]);

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '400px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" title="Error Loading Dashboard" message={error} className="tw-m-4" />
    );
  }

  return (
    <div className="user-dashboard">
      {/* Summary Cards */}
      <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
        {summaryCards.map((card, index) => (
          <div key={index} className="tw-col-span-12 sm:tw-col-span-6 lg:tw-col-span-4 xl:tw-col-span-2">
            <SummaryCard {...card} />
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
        {/* Monthly Growth Chart */}
        <div className="tw-col-span-12 xl:tw-col-span-7">
          <div className="chart-card tw-shadow-sm tw-h-full">
            <div className="tw-p-4">
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="chart-icon-wrapper tw-mr-3">
                  <FaChartLine className="chart-icon" />
                </div>
                <div>
                  <h5 className="tw-mb-0 tw-font-bold">User Growth Trend</h5>
                  <p className="tw-text-slate-500 tw-mb-0 tw-text-xs">New and active users over last 12 months</p>
                </div>
              </div>
              <ReactECharts
                option={monthlyGrowthChart}
                style={{ height: '340px', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
          </div>
        </div>

        {/* Users by Site */}
        <div className="tw-col-span-12 xl:tw-col-span-5">
          <div className="chart-card tw-shadow-sm tw-h-full">
            <div className="tw-p-4">
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="chart-icon-wrapper tw-mr-3">
                  <FaSitemap className="chart-icon" />
                </div>
                <div>
                  <h5 className="tw-mb-0 tw-font-bold">Users by Site</h5>
                  <p className="tw-text-slate-500 tw-mb-0 tw-text-xs">Distribution across office locations</p>
                </div>
              </div>
              {statistics?.users_by_site?.length > 0 ? (
                <ReactECharts
                  option={usersBySiteChart}
                  style={{ height: '340px', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              ) : (
                <div className="tw-flex tw-justify-center tw-items-center" style={{ height: '340px' }}>
                  <p className="tw-text-slate-500">No site data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row of Charts */}
      <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
        {/* Users by Role */}
        <div className="tw-col-span-12 md:tw-col-span-6">
          <div className="chart-card tw-shadow-sm tw-h-full">
            <div className="tw-p-4">
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="chart-icon-wrapper tw-mr-3">
                  <FaUserShield className="chart-icon" />
                </div>
                <div>
                  <h5 className="tw-mb-0 tw-font-bold">Users by Role</h5>
                  <p className="tw-text-slate-500 tw-mb-0 tw-text-xs">Role distribution overview</p>
                </div>
              </div>
              {statistics?.users_by_role?.length > 0 ? (
                <ReactECharts
                  option={usersByRoleChart}
                  style={{ height: '300px', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              ) : (
                <div className="tw-flex tw-justify-center tw-items-center" style={{ height: '300px' }}>
                  <p className="tw-text-slate-500">No role data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Platform Accounts Summary */}
        <div className="tw-col-span-12 md:tw-col-span-6">
          <div className="chart-card tw-shadow-sm tw-h-full">
            <div className="tw-p-4">
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="chart-icon-wrapper tw-mr-3">
                  <FaUsers className="chart-icon" />
                </div>
                <div>
                  <h5 className="tw-mb-0 tw-font-bold">Platform Account Status</h5>
                  <p className="tw-text-slate-500 tw-mb-0 tw-text-xs">External platform integrations</p>
                </div>
              </div>
              {statistics?.platform_accounts ? (
                <div className="platform-accounts-grid">
                  {Object.entries(statistics.platform_accounts).map(([platform, data]) => (
                    <div key={platform} className="platform-account-item">
                      <div className="platform-name">{platform.replace(/_/g, ' ').toUpperCase()}</div>
                      <div className="platform-stats">
                        <div className="stat-item">
                          <span className="stat-label">Total Accounts:</span>
                          <span className="stat-value">{data.total || 0}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Active:</span>
                          <span className="stat-value tw-text-green-600">{data.active || 0}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Inactive:</span>
                          <span className="stat-value tw-text-red-600">{(data.total || 0) - (data.active || 0)}</span>
                        </div>
                      </div>
                      <ProgressBar
                        now={data.total > 0 ? (data.active / data.total) * 100 : 0}
                        variant="success"
                        size="sm"
                        className="tw-mt-2"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tw-flex tw-justify-center tw-items-center" style={{ height: '300px' }}>
                  <p className="tw-text-slate-500">No platform data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Tables */}
      <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
        {/* Recently Active Users */}
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <div className="data-table-card tw-shadow-sm tw-h-full">
            <div className="tw-flex-1 tw-p-4">
              <h5 className="tw-mb-3 tw-font-bold">Recently Active Users</h5>
              {recently_active && recently_active.length > 0 ? (
                <div className="tw-overflow-x-auto">
                  <table className="activity-table tw-w-full tw-mb-0 tw-text-xs">
                    <thead>
                      <tr>
                        <th className="tw-text-left tw-py-2 tw-px-2">Name</th>
                        <th className="tw-text-left tw-py-2 tw-px-2">Email</th>
                        <th className="tw-text-left tw-py-2 tw-px-2">Last Active</th>
                        <th className="tw-text-center tw-py-2 tw-px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recently_active.map((user) => (
                        <tr key={user.id} className="hover:tw-bg-slate-50 tw-border-b tw-border-slate-100">
                          <td className="tw-font-semibold tw-py-2 tw-px-2"><Link to={`/authorization/users/${user.id}`} target="_blank" rel="noopener noreferrer">{user.full_name} ({user.name})</Link></td>
                          <td className="tw-text-slate-500 tw-py-2 tw-px-2">{user.email}</td>
                          <td className="tw-text-slate-500 tw-py-2 tw-px-2">{dayjs(user.last_active).fromNow()}</td>
                          <td className="tw-text-center tw-py-2 tw-px-2">
                            {user.online ? (
                              <Badge variant="success" size="sm">Online</Badge>
                            ) : (
                              <Badge variant="default" size="sm">Offline</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="tw-text-center tw-text-slate-500 tw-py-12">
                  <p>No recently active users</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Frequently Accessed Users */}
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <div className="data-table-card tw-shadow-sm tw-h-full">
            <div className="tw-flex-1 tw-p-4">
              <h5 className="tw-mb-3 tw-font-bold">Frequently Accessed Users (Last 30 Days)</h5>
              {frequently_accessed && frequently_accessed.length > 0 ? (
                <div className="tw-overflow-x-auto">
                  <table className="activity-table tw-w-full tw-mb-0 tw-text-xs">
                    <thead>
                      <tr>
                        <th className="tw-text-left tw-py-2 tw-px-2">Name</th>
                        <th className="tw-text-left tw-py-2 tw-px-2">Email</th>
                        <th className="tw-text-center tw-py-2 tw-px-2">Access Count</th>
                        <th className="tw-text-left tw-py-2 tw-px-2">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {frequently_accessed.map((user) => (
                        <tr key={user.id} className="hover:tw-bg-slate-50 tw-border-b tw-border-slate-100">
                          <td className="tw-font-semibold tw-py-2 tw-px-2"><Link to={`/authorization/users/${user.id}`} target="_blank" rel="noopener noreferrer">{user.full_name} ({user.name})</Link></td>
                          <td className="tw-text-slate-500 tw-py-2 tw-px-2">{user.email}</td>
                          <td className="tw-text-center tw-py-2 tw-px-2">
                            <Badge variant="primary" size="sm">{user.access_count}</Badge>
                          </td>
                          <td className="tw-text-slate-500 tw-py-2 tw-px-2">{dayjs(user.last_active).fromNow()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="tw-text-center tw-text-slate-500 tw-py-12">
                  <p>No access data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inactive Users with Platform Access */}
      <div className="tw-mb-4">
        <div className="data-table-card tw-shadow-sm">
          <div className="tw-flex-1 tw-p-4">
            <div className="tw-flex tw-items-center tw-mb-3">
              <div className="chart-icon-wrapper tw-mr-3">
                <FaUserShield className="chart-icon" style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <h5 className="tw-mb-0 tw-font-bold">Inactive Users with Active Platform Access</h5>
                <p className="tw-text-slate-500 tw-mb-0 tw-text-xs">Portal inactive but active on external platforms</p>
              </div>
            </div>
            {inactive_with_platform_access && inactive_with_platform_access.length > 0 ? (
              <div className="tw-overflow-x-auto">
                <table className="activity-table tw-w-full tw-mb-0 tw-text-xs">
                  <thead>
                    <tr>
                      <th className="tw-text-left tw-py-2 tw-px-2">Name</th>
                      <th className="tw-text-left tw-py-2 tw-px-2">Email</th>
                      <th className="tw-text-left tw-py-2 tw-px-2">Active Platforms</th>
                      <th className="tw-text-left tw-py-2 tw-px-2">Created</th>
                      <th className="tw-text-left tw-py-2 tw-px-2">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactive_with_platform_access.map((user) => (
                      <tr key={user.id} className="hover:tw-bg-slate-50 tw-border-b tw-border-yellow-200">
                        <td className="tw-font-semibold tw-py-2 tw-px-2"><Link to={`/authorization/users/${user.id}`} target="_blank" rel="noopener noreferrer">{user.full_name} ({user.name})</Link></td>
                        <td className="tw-text-slate-500 tw-py-2 tw-px-2">{user.email}</td>
                        <td className="tw-py-2 tw-px-2">
                          {user.platforms.map((platform) => (
                            <Badge key={platform} variant="warning" size="sm" className="tw-mr-2 tw-mb-1">
                              {platform}
                            </Badge>
                          ))}
                        </td>
                        <td className="tw-text-slate-500 tw-py-2 tw-px-2">{dayjs(user.created_at).format('MMM DD, YYYY')}</td>
                        <td className="tw-text-slate-500 tw-py-2 tw-px-2">{dayjs(user.updated_at).fromNow()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="tw-text-center tw-text-slate-500 tw-py-12">
                <p>No inactive users with platform access</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;